import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  TrendingUp,
  DollarSign,
  Target,
  Zap,
  Globe,
  BarChart3,
  CheckCircle,
  AlertTriangle,
  Info,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Loader2,
  History,
} from "lucide-react";
import { toast } from "sonner";

const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

export default function FloorOptimizerPage() {
  const { api } = useAuth();
  const [ssps, setSsps] = useState([]);
  const [selectedSsp, setSelectedSsp] = useState("all");
  const [days, setDays] = useState("7");
  const [stats, setStats] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applyingFloor, setApplyingFloor] = useState(null);
  const [confirmApply, setConfirmApply] = useState(null);

  useEffect(() => {
    fetchSSPs();
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedSsp, days]);

  const fetchSSPs = async () => {
    try {
      const response = await api.get("/ssps");
      setSsps(response.data);
    } catch (error) {
      toast.error("Failed to load SSPs");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const sspParam = selectedSsp && selectedSsp !== "all" ? `&ssp_id=${selectedSsp}` : "";
      const [statsRes, recRes, historyRes] = await Promise.all([
        api.get(`/optimizer/stats?${sspParam}`),
        api.get(`/optimizer/analyze?days=${days}${sspParam}`),
        api.get(`/optimizer/history?limit=20${sspParam}`),
      ]);
      setStats(statsRes.data);
      setRecommendations(recRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      toast.error("Failed to load optimizer data");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFloor = async () => {
    if (!confirmApply || !selectedSsp || selectedSsp === "all") {
      toast.error("Please select a specific SSP to apply floor changes");
      return;
    }
    
    setApplyingFloor(confirmApply);
    try {
      await api.post("/optimizer/apply", null, {
        params: {
          ssp_id: selectedSsp,
          country: confirmApply.country,
          ad_format: confirmApply.ad_format,
          new_floor: confirmApply.suggested_floor,
        },
      });
      toast.success("Floor price updated successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to apply floor price");
    } finally {
      setApplyingFloor(null);
      setConfirmApply(null);
    }
  };

  const getConfidenceBadge = (confidence) => {
    const variants = {
      High: "bg-emerald-500/10 text-emerald-500",
      Medium: "bg-amber-500/10 text-amber-500",
      Low: "bg-zinc-500/10 text-zinc-500",
    };
    return (
      <Badge className={variants[confidence] || variants.Low} variant="outline">
        {confidence}
      </Badge>
    );
  };

  const formatCurrency = (value) => `$${value.toFixed(4)}`;
  const formatPercent = (value) => `${value.toFixed(2)}%`;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover text-popover-foreground border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">{entry.name}:</span>
              <span className="font-mono font-medium">
                {entry.name.includes("Revenue") || entry.name.includes("revenue")
                  ? formatCurrency(entry.value)
                  : entry.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="floor-optimizer-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Target className="h-8 w-8 text-accent" />
            Bid Floor Optimizer
          </h1>
          <p className="text-muted-foreground">
            AI-powered floor price recommendations to maximize revenue
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedSsp} onValueChange={setSelectedSsp}>
            <SelectTrigger className="w-[200px]" data-testid="ssp-filter">
              <SelectValue placeholder="All SSPs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All SSPs</SelectItem>
              {ssps.map((ssp) => (
                <SelectItem key={ssp.id} value={ssp.id}>
                  {ssp.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[150px]" data-testid="days-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {loading && !stats ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      ) : (
        <>
          {/* Stats Overview */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Zap className="h-4 w-4" />
                  <span className="text-sm">Total Bids</span>
                </div>
                <p className="text-2xl font-bold font-mono">{stats.total_bids}</p>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <span className="text-sm">Won</span>
                </div>
                <p className="text-2xl font-bold font-mono text-emerald-500">{stats.total_won}</p>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <DollarSign className="h-4 w-4" />
                  <span className="text-sm">Revenue</span>
                </div>
                <p className="text-2xl font-bold font-mono">${stats.total_revenue.toFixed(2)}</p>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <TrendingUp className="h-4 w-4" />
                  <span className="text-sm">Avg Bid</span>
                </div>
                <p className="text-2xl font-bold font-mono">${stats.avg_bid_price.toFixed(4)}</p>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Target className="h-4 w-4" />
                  <span className="text-sm">Win Rate</span>
                </div>
                <p className="text-2xl font-bold font-mono">{stats.avg_win_rate.toFixed(1)}%</p>
              </Card>
            </div>
          )}

          {/* Charts Row */}
          {stats && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue by Country */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5 text-accent" />
                    Revenue by Country
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.by_country.slice(0, 8)}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis
                          dataKey="country"
                          className="text-xs"
                          tick={{ fill: "hsl(var(--muted-foreground))" }}
                        />
                        <YAxis
                          className="text-xs"
                          tick={{ fill: "hsl(var(--muted-foreground))" }}
                          tickFormatter={(v) => `$${v}`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="revenue" name="Revenue" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Win Rate by Format */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-accent" />
                    Performance by Ad Format
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.by_format} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis type="number" tick={{ fill: "hsl(var(--muted-foreground))" }} />
                        <YAxis
                          type="category"
                          dataKey="format"
                          className="text-xs"
                          tick={{ fill: "hsl(var(--muted-foreground))" }}
                          width={80}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="win_rate" name="Win Rate %" fill="hsl(var(--chart-2))" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Recommendations */}
          {recommendations && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-accent" />
                      Floor Price Recommendations
                    </CardTitle>
                    <CardDescription>
                      Based on analysis of {recommendations.total_analyzed} bid requests
                    </CardDescription>
                  </div>
                  {recommendations.total_potential_increase > 0 && (
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Potential Increase</p>
                      <p className="text-2xl font-bold text-emerald-500 flex items-center gap-1">
                        <ArrowUpRight className="h-5 w-5" />
                        {recommendations.total_potential_increase.toFixed(1)}%
                      </p>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {recommendations.recommendations.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Info className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="font-medium">Not enough data for recommendations</p>
                    <p className="text-sm">Run more bid simulations to generate floor price suggestions</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Country</TableHead>
                        <TableHead>Ad Format</TableHead>
                        <TableHead className="text-right">Current Floor</TableHead>
                        <TableHead className="text-right">Suggested Floor</TableHead>
                        <TableHead className="text-right">Potential Impact</TableHead>
                        <TableHead className="text-right">Win Rate</TableHead>
                        <TableHead className="text-right">Sample Size</TableHead>
                        <TableHead>Confidence</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recommendations.recommendations.map((rec, index) => (
                        <TableRow key={`${rec.country}-${rec.ad_format}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Globe className="h-4 w-4 text-muted-foreground" />
                              {rec.country}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{rec.ad_format}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            ${rec.current_floor.toFixed(4)}
                          </TableCell>
                          <TableCell className="text-right font-mono font-medium">
                            ${rec.suggested_floor.toFixed(4)}
                          </TableCell>
                          <TableCell className="text-right">
                            <span
                              className={`font-mono font-medium flex items-center justify-end gap-1 ${
                                rec.potential_revenue_increase > 0
                                  ? "text-emerald-500"
                                  : rec.potential_revenue_increase < 0
                                  ? "text-red-500"
                                  : ""
                              }`}
                            >
                              {rec.potential_revenue_increase > 0 ? (
                                <ArrowUpRight className="h-4 w-4" />
                              ) : rec.potential_revenue_increase < 0 ? (
                                <ArrowDownRight className="h-4 w-4" />
                              ) : null}
                              {rec.potential_revenue_increase.toFixed(1)}%
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {rec.win_rate.toFixed(1)}%
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {rec.sample_size}
                          </TableCell>
                          <TableCell>{getConfidenceBadge(rec.confidence)}</TableCell>
                          <TableCell>
                            {selectedSsp && selectedSsp !== "all" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setConfirmApply(rec)}
                                disabled={applyingFloor === rec}
                                data-testid={`apply-floor-${rec.country}-${rec.ad_format}`}
                              >
                                {applyingFloor === rec ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  "Apply"
                                )}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}

          {/* Optimization History */}
          {history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5 text-accent" />
                  Optimization History
                </CardTitle>
                <CardDescription>Recent floor price changes</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>SSP</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead>Ad Format</TableHead>
                      <TableHead className="text-right">Old Floor</TableHead>
                      <TableHead className="text-right">New Floor</TableHead>
                      <TableHead className="text-right">Change</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((h) => {
                      const change = ((h.new_floor - h.old_floor) / h.old_floor) * 100;
                      return (
                        <TableRow key={h.id}>
                          <TableCell className="font-mono text-xs">
                            {new Date(h.applied_at).toLocaleString()}
                          </TableCell>
                          <TableCell>{h.ssp_id.slice(0, 8)}...</TableCell>
                          <TableCell>{h.country}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{h.ad_format}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            ${h.old_floor.toFixed(4)}
                          </TableCell>
                          <TableCell className="text-right font-mono font-medium">
                            ${h.new_floor.toFixed(4)}
                          </TableCell>
                          <TableCell
                            className={`text-right font-mono ${
                              change > 0 ? "text-emerald-500" : "text-red-500"
                            }`}
                          >
                            {change > 0 ? "+" : ""}
                            {change.toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Confirm Apply Dialog */}
      <AlertDialog open={!!confirmApply} onOpenChange={() => setConfirmApply(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apply Floor Price Recommendation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to update the floor price?
              <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Country:</span>
                  <span className="font-medium">{confirmApply?.country}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Ad Format:</span>
                  <span className="font-medium">{confirmApply?.ad_format}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Current Floor:</span>
                  <span className="font-mono">${confirmApply?.current_floor.toFixed(4)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>New Floor:</span>
                  <span className="font-mono font-medium text-accent">
                    ${confirmApply?.suggested_floor.toFixed(4)}
                  </span>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleApplyFloor}>Apply Changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
